// queries.ts
// React Query hooks for Tours module

import { useQuery } from "@tanstack/react-query";

import { listTours, getTourById } from "./api";
import type {
  ListToursData,
  ListToursQueryParams,
  TourDetail,
} from "./types";

/* =========================================================
 * Query Keys
 * ======================================================= */

export const ToursKeys = {
  all: ["tours"] as const,

  lists: () => [...ToursKeys.all, "list"] as const,
  list: (params: ListToursQueryParams = {}) =>
    [...ToursKeys.lists(), params] as const,

  details: () => [...ToursKeys.all, "detail"] as const,
  detail: (id: number) => [...ToursKeys.details(), id] as const,
};

/* =========================================================
 * LIST – useToursList
 * ======================================================= */

/**
 * Fetch paginated list of tours (with tourType & supplier relations).
 */
export function useToursList(query: ListToursQueryParams = {}) {
  return useQuery<ListToursData, Error>({
    queryKey: ToursKeys.list(query),
    queryFn: () => listTours(query),
    // Keep previous page data while refetching (v5 default-friendly pattern)
    placeholderData: (prev) => prev,
  });
}

/* =========================================================
 * DETAIL – useTourDetail
 * ======================================================= */

/**
 * Fetch a single tour with all nested details.
 */
export function useTourDetail(id: number, enabled: boolean = true) {
  return useQuery<TourDetail, Error>({
    queryKey: ToursKeys.detail(id),
    queryFn: () => getTourById(id),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}
